import { useSearchParams } from 'react-router-dom';
import {
  DRAWER_TAB_ACTIVE_CLASSES,
  DRAWER_TAB_INACTIVE_CLASSES,
  DRAWER_TAB_LINK_CLASSES,
} from './drawerTabStyles';

export type DrawerContext = 'objective' | 'result';

type DrawerTabBarProps = {
  context: DrawerContext;
  activeTab: string;
  /**
   * Feature flags for optional tabs.
   *
   * Note: `enableStatusDrawer` was intentionally removed in mr-aqs.14 when
   * StatusCell3 became the default renderer for all maps. Only `enableDescendants`
   * remains as a guarded flag pending wider rollout.
   */
  featureFlags?: {
    enableDescendants?: boolean;
  };
  canViewAllResults?: boolean;
  canViewComments?: boolean;
};

/**
 * DrawerTabBar — Presentational tab bar for Map3Drawer.
 *
 * Renders context-dependent tabs:
 * - objective: full tab set (Info, Status, Descendants, Results, Comments, Dependencies, Settings, Team)
 * - result: hidden entirely (ResultTab manages its own sub-tabs via useState)
 *
 * Navigation uses setSearchParams (functional form) to preserve all other params
 * (e.g. ?view=, ?zoomLevel=, ?forInterval=, ?edit=).
 */
export function DrawerTabBar({
  context,
  activeTab,
  featureFlags,
  canViewAllResults,
  canViewComments,
}: DrawerTabBarProps) {
  const [, setSearchParams] = useSearchParams();

  // Result context: drawer-level tab bar is hidden entirely
  if (context === 'result') {
    return null;
  }

  const handleTabClick = (tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      // Clear result-specific params when switching to a non-result tab
      next.delete('resultId');
      return next;
    });
  };

  const tabClass = (tab: string) =>
    `${DRAWER_TAB_LINK_CLASSES} ${activeTab === tab ? DRAWER_TAB_ACTIVE_CLASSES : DRAWER_TAB_INACTIVE_CLASSES}`;

  // Objective context: full tab set
  return (
    <nav className="bg-white" aria-label="Objective Details">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 justify-between">
          <div className="flex w-full">
            <div className="flex w-full justify-between md:space-x-8">
              {/* Info tab - always visible */}
              <button
                onClick={() => handleTabClick('info')}
                className={tabClass('info')}
                aria-current={activeTab === 'info' ? 'page' : undefined}
              >
                Info
              </button>

              {/* Status tab - always visible */}
              <button
                onClick={() => handleTabClick('status')}
                className={tabClass('status')}
                aria-current={activeTab === 'status' ? 'page' : undefined}
              >
                Status
              </button>

              {/* Descendants tab - only show if feature flag is enabled */}
              {featureFlags?.enableDescendants && (
                <button
                  onClick={() => handleTabClick('descendants')}
                  className={tabClass('descendants')}
                  aria-current={activeTab === 'descendants' ? 'page' : undefined}
                >
                  Descendants
                </button>
              )}

              {/* Results tab - only show if user can view results */}
              {canViewAllResults && (
                <button
                  onClick={() => handleTabClick('results')}
                  className={tabClass('results')}
                  aria-current={activeTab === 'results' ? 'page' : undefined}
                >
                  Results
                </button>
              )}

              {/* Comments tab - only show if user can view comments */}
              {canViewComments && (
                <button
                  onClick={() => handleTabClick('comments')}
                  className={tabClass('comments')}
                  aria-current={activeTab === 'comments' ? 'page' : undefined}
                >
                  Comments
                </button>
              )}

              {/* Settings tab */}
              <button
                onClick={() => handleTabClick('settings')}
                className={tabClass('settings')}
                aria-current={activeTab === 'settings' ? 'page' : undefined}
              >
                Settings
              </button>

              {/* Team tab */}
              <button
                onClick={() => handleTabClick('team')}
                className={tabClass('team')}
                aria-current={activeTab === 'team' ? 'page' : undefined}
              >
                Team
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
